/**
 * wormholeTunnelPipeline — 3D wormhole tunnel with chromatic aberration.
 *
 * A raymarched tunnel where the camera flies forward through a
 * twisted tube. The tube has a swirling pattern on its walls, and
 * chromatic aberration (RGB split) gives a sci-fi feel. Audio drives
 * flight speed and color saturation.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface WormholeTunnelPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// Ray-cylinder intersection: returns distance to cylinder of radius r
// along z-axis. We simplify by considering only the inner wall distance.
vec2 rayCylinder(vec3 ro, vec3 rd, float r) {
  // We're inside the cylinder. Distance to the inner wall is r - length(rd.xy * t + ro.xy).
  // Compute the t at which the ray hits the wall.
  // (rd.x^2 + rd.y^2) t^2 + 2(ro.x*rd.x + ro.y*rd.y) t + ro.x^2 + ro.y^2 - r^2 = 0
  float a = rd.x * rd.x + rd.y * rd.y;
  float b = ro.x * rd.x + ro.y * rd.y;
  float c = ro.x * ro.x + ro.y * ro.y - r * r;
  float disc = b * b - a * c;
  if (disc < 0.0) return vec2(-1.0, -1.0);
  float sqrtDisc = sqrt(disc);
  return vec2(
    (-b - sqrtDisc) / a,
    (-b + sqrtDisc) / a
  );
}

vec3 tunnelColor(vec3 hitP, vec2 channelOffset) {
  // Twisting tunnel: rotate around z as we move forward.
  float twist = hitP.z * 0.05 + uTime * 0.1;
  float ca = cos(twist), sa = sin(twist);
  vec2 tp = vec2(ca * hitP.x - sa * hitP.y, sa * hitP.x + ca * hitP.y);
  // Add the channel offset (chromatic aberration).
  tp += channelOffset;
  // Pattern on the wall: stripes + noise.
  float stripe = fract(tp.y * 5.0 + hitP.z * 0.3);
  float pattern = valueNoise(tp * 2.0 + hitP.z * 0.1);
  // Color based on pattern.
  vec3 col1 = vec3(0.30, 0.65, 0.95);
  vec3 col2 = vec3(0.95, 0.30, 0.75);
  vec3 col = mix(col1, col2, pattern);
  col *= 0.5 + 0.5 * stripe;
  // Add bright stripes.
  col += vec3(0.95, 0.85, 0.50) * (1.0 - abs(stripe - 0.5) * 4.0) * 0.4;
  return col;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Camera setup: flying forward through the tunnel.
  // Camera moves at speed (1 + bass).
  float speed = 1.0 + bass * 2.0;
  vec3 ro = vec3(0.0, 0.0, uTime * speed);
  // Ray direction: looking forward with screen-space offset.
  vec3 rd = normalize(vec3(p * 0.8, -1.0));

  // Tunnel radius.
  float tunnelRadius = 0.8;

  // Compute the t at which the ray hits the tunnel wall.
  vec2 ts = rayCylinder(ro, rd, tunnelRadius);
  float t = (ts.x > 0.0) ? ts.x : ts.y;
  if (t < 0.0) t = 8.0;  // miss

  // Hit point on the tunnel wall.
  vec3 hitP = ro + rd * t;

  // Chromatic aberration: sample each channel at a slightly different
  // radius.
  float chroma = 0.04 + high * 0.06;
  vec3 col;
  col.r = tunnelColor(hitP, vec2(chroma, 0.0)).r;
  col.g = tunnelColor(hitP, vec2(0.0, 0.0)).g;
  col.b = tunnelColor(hitP, vec2(-chroma, 0.0)).b;

  // Depth fade (further = darker, with magenta tint).
  float depthFade = exp(-t * 0.05);
  col = mix(vec3(0.05, 0.02, 0.10), col, depthFade);

  // Audio: pulse.
  col *= 1.0 + mid * 0.3;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeWormholeTunnelBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeWormholeTunnelRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createWormholeTunnelPipeline(gl: WebGL2RenderingContext): WormholeTunnelPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawWormholeTunnel(
  gl: WebGL2RenderingContext,
  pipe: WormholeTunnelPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
